import React, { useState } from "react";
import { PROFESSIONAL_FIELDS } from "../../utils/constants";

/**
 * Profession picker.
 *
 * The common case is one profession, so that is all this shows: a plain
 * dropdown, identical to what it replaced. Someone who supervises more than one
 * profession (e.g. a practice instructor with Informatik and KV apprentices)
 * opens the optional "other professions" section and ticks the extras.
 *
 * `value` is the primary profession, `additional` the extra ones. The caller
 * submits `professionType: value` plus `professions: [value, ...additional]`;
 * a single-profession user therefore sends exactly what it always sent.
 */
function ProfessionSelect({
  value,
  additional = [],
  onChange,
  allowMultiple = false,
  disabled = false,
  error,
  label = "Profession",
  name = "professionType",
  className = "",
}) {
  const handlePrimaryChange = (event) => {
    const nextPrimary = event.target.value;
    // The primary is never also an "additional" one.
    onChange({
      professionType: nextPrimary,
      additional: additional.filter((id) => id !== nextPrimary),
    });
  };

  return (
    <div className={className}>
      <label className="label-brand" htmlFor={name}>
        {label}
      </label>
      <select
        id={name}
        name={name}
        value={value || ""}
        onChange={handlePrimaryChange}
        disabled={disabled}
        className={`input-brand ${error ? "input-brand-error" : ""}`}
      >
        <option value="">Select Profession</option>
        {PROFESSIONAL_FIELDS.map((field) => (
          <option key={field.id} value={field.id}>
            {field.label}
          </option>
        ))}
      </select>
      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}

      {allowMultiple && !disabled && (
        <ProfessionSelectExtras
          primary={value}
          additional={additional}
          onChange={(next) => onChange({ professionType: value, additional: next })}
        />
      )}
    </div>
  );
}

/**
 * The "other professions" half on its own, for forms where the primary profession
 * is fixed (e.g. an invitation that already presets it).
 */
export function ProfessionSelectExtras({ primary, additional = [], onChange }) {
  const [expanded, setExpanded] = useState(additional.length > 0);
  const extras = PROFESSIONAL_FIELDS.filter((field) => field.id !== primary);

  const toggle = (id) => {
    onChange(
      additional.includes(id)
        ? additional.filter((entry) => entry !== id)
        : [...additional, id],
    );
  };

  return (
    <div className="mt-2">
      {!expanded ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-sm text-sunrise-red hover:underline"
        >
          + I also supervise other professions
        </button>
      ) : (
        <fieldset className="border border-gray-200 rounded-md p-3">
          <legend className="text-sm text-gray-600 px-1">
            Other Professions (optional)
          </legend>
          <div className="flex flex-wrap gap-x-4 gap-y-2">
            {extras.map((field) => (
              <label
                key={field.id}
                className="flex items-center gap-2 text-sm text-gray-700"
              >
                <input
                  type="checkbox"
                  checked={additional.includes(field.id)}
                  onChange={() => toggle(field.id)}
                />
                {field.label}
              </label>
            ))}
          </div>
        </fieldset>
      )}
    </div>
  );
}

/** The value to submit as `professions`: the primary first, then the extras. */
export function toProfessions(professionType, additional = []) {
  if (!professionType) {
    return [];
  }
  return [professionType, ...additional.filter((id) => id !== professionType)];
}

/** Splits a `professions` array from the API back into primary + additional. */
export function fromProfessions(professionType, professions = []) {
  return (professions || []).filter((id) => id !== professionType);
}

export default ProfessionSelect;
